import type { Knex } from 'knex'

/**
 * Supported database types.
 */
export type DatabaseType = 'sqlite' | 'postgresql' | 'mysql'

/**
 * Column definition from database.
 */
export interface ColumnInfo {
  name: string
  type: string
  nullable: boolean
  defaultValue: string | null
  primaryKey: boolean
}

/**
 * Table definition from database.
 */
export interface TableInfo {
  name: string
  columns: Map<string, ColumnInfo>
  indexes: Array<{ name: string; columns: string[]; unique: boolean }>
  foreignKeys: Array<{
    column: string
    referencedTable: string
    referencedColumn: string
    onDelete?: string
    onUpdate?: string
  }>
}

/**
 * Database schema.
 */
export interface DatabaseSchema {
  tables: Map<string, TableInfo>
}

/**
 * Detect database type from Knex client.
 */
export function detectDatabaseType(knex: Knex): DatabaseType {
  const client = knex.client.config.client
  if (client === 'better-sqlite3' || client === 'sqlite3' || client === 'sqlite') {
    return 'sqlite'
  }
  if (client === 'pg' || client === 'postgresql') {
    return 'postgresql'
  }
  if (client === 'mysql' || client === 'mysql2') {
    return 'mysql'
  }
  throw new Error(`Unsupported database client: ${client}`)
}

/**
 * Read current database schema.
 * Supports SQLite, PostgreSQL, and MySQL.
 * @param knex - Knex instance
 * @param dbType - Optional database type override (auto-detected if not provided)
 */
export async function readDatabaseSchema(knex: Knex, dbType?: DatabaseType): Promise<DatabaseSchema> {
  const effectiveType = dbType ?? detectDatabaseType(knex)

  switch (effectiveType) {
    case 'sqlite':
      return readSQLiteSchema(knex)
    case 'postgresql':
      return readPostgreSQLSchema(knex)
    case 'mysql':
      return readMySQLSchema(knex)
    default:
      throw new Error(`Unsupported database type: ${effectiveType}`)
  }
}

/**
 * Read SQLite schema.
 * Uses PRAGMA commands to introspect the database.
 */
async function readSQLiteSchema(knex: Knex): Promise<DatabaseSchema> {
  const tables = new Map<string, TableInfo>()

  // Get all tables (excluding system tables)
  const tableList = await knex.raw<{ name: string; type: string }[]>(
    "SELECT name, type FROM sqlite_master WHERE type='table' ORDER BY name"
  )

  for (const tableRow of tableList) {
    const tableName = tableRow.name

    // Skip system tables
    if (isSystemTable(tableName)) continue

    // Get columns
    const columnsRaw = await knex.raw<
      Array<{
        cid: number
        name: string
        type: string
        notnull: number
        dflt_value: string | null
        pk: number
      }>
    >(`PRAGMA table_info('${tableName}')`)

    const columns = new Map<string, ColumnInfo>()
    for (const col of columnsRaw) {
      columns.set(col.name, {
        name: col.name,
        type: col.type.toUpperCase(),
        nullable: col.notnull === 0,
        defaultValue: col.dflt_value,
        primaryKey: col.pk > 0,
      })
    }

    // Get indexes
    const indexListRaw = await knex.raw<
      Array<{
        seq: number
        name: string
        unique: number
        origin: string
        partial: number
      }>
    >(`PRAGMA index_list('${tableName}')`)

    const indexes: TableInfo['indexes'] = []
    for (const idx of indexListRaw) {
      // Skip auto-generated primary key indexes
      if (idx.origin === 'pk') continue

      const indexInfoRaw = await knex.raw<Array<{ seqno: number; cid: number; name: string }>>(
        `PRAGMA index_info('${idx.name}')`
      )

      const indexColumns = indexInfoRaw.map((info) => info.name)

      indexes.push({
        name: idx.name,
        columns: indexColumns,
        unique: idx.unique === 1,
      })
    }

    // Get foreign keys
    const foreignKeysRaw = await knex.raw<
      Array<{
        id: number
        seq: number
        table: string
        from: string
        to: string
        on_update: string
        on_delete: string
      }>
    >(`PRAGMA foreign_key_list('${tableName}')`)

    const foreignKeys: TableInfo['foreignKeys'] = []
    const processedFks = new Set<number>()

    for (const fk of foreignKeysRaw) {
      if (processedFks.has(fk.id)) continue
      processedFks.add(fk.id)

      foreignKeys.push({
        column: fk.from,
        referencedTable: fk.table,
        referencedColumn: fk.to,
        onDelete: fk.on_delete !== 'NO ACTION' ? fk.on_delete : undefined,
        onUpdate: fk.on_update !== 'NO ACTION' ? fk.on_update : undefined,
      })
    }

    tables.set(tableName, {
      name: tableName,
      columns,
      indexes,
      foreignKeys,
    })
  }

  return { tables }
}

/**
 * Read PostgreSQL schema using information_schema.
 */
async function readPostgreSQLSchema(knex: Knex): Promise<DatabaseSchema> {
  const tables = new Map<string, TableInfo>()

  // Get all tables
  const tableRows = await knex('information_schema.tables')
    .select('table_name')
    .where({ table_schema: 'public', table_type: 'BASE TABLE' })
    .orderBy('table_name')

  for (const row of tableRows) {
    const tableName = row.table_name
    if (isSystemTable(tableName)) continue

    // Get columns
    const columnRows = await knex('information_schema.columns')
      .select('column_name', 'data_type', 'is_nullable', 'column_default')
      .where({ table_schema: 'public', table_name: tableName })
      .orderBy('ordinal_position')

    const columns = new Map<string, ColumnInfo>()
    for (const col of columnRows) {
      columns.set(col.column_name, {
        name: col.column_name,
        type: col.data_type.toUpperCase(),
        nullable: col.is_nullable === 'YES',
        defaultValue: col.column_default,
        primaryKey: false, // Will be set below
      })
    }

    // Get primary keys
    const pkRows = await knex('information_schema.key_column_usage')
      .select('column_name')
      .where({
        table_schema: 'public',
        table_name: tableName,
        constraint_name: `${tableName}_pkey`,
      })

    for (const pk of pkRows) {
      const col = columns.get(pk.column_name)
      if (col) col.primaryKey = true
    }

    // Get indexes
    const indexes: TableInfo['indexes'] = []
    const idxResult = await knex.raw(
      `
      SELECT
        i.relname AS index_name,
        ix.indisunique AS is_unique,
        array_agg(a.attname ORDER BY k.n) AS column_names
      FROM pg_index ix
      JOIN pg_class t ON t.oid = ix.indrelid
      JOIN pg_class i ON i.oid = ix.indexrelid
      JOIN pg_namespace n ON n.oid = t.relnamespace
      CROSS JOIN LATERAL unnest(ix.indkey) WITH ORDINALITY AS k(attnum, n)
      JOIN pg_attribute a ON a.attrelid = t.oid AND a.attnum = k.attnum
      WHERE n.nspname = 'public'
        AND t.relname = ?
        AND NOT ix.indisprimary
      GROUP BY i.relname, ix.indisunique
      `,
      [tableName]
    )
    const idxRows = idxResult.rows || []
    for (const idx of idxRows) {
      // pg driver returns array_agg as string "{col1,col2}" instead of JS array
      const cols = Array.isArray(idx.column_names)
        ? idx.column_names
        : typeof idx.column_names === 'string'
          ? idx.column_names.replace(/^\{|\}$/g, '').split(',').filter(Boolean)
          : []
      indexes.push({
        name: idx.index_name,
        columns: cols,
        unique: idx.is_unique,
      })
    }

    // Get foreign keys (including ON DELETE / ON UPDATE rules)
    const foreignKeys: TableInfo['foreignKeys'] = []
    const fkResult = await knex.raw(
      `
      SELECT
        kcu.column_name,
        ccu.table_name AS foreign_table_name,
        ccu.column_name AS foreign_column_name,
        rc.delete_rule,
        rc.update_rule
      FROM information_schema.table_constraints AS tc
      JOIN information_schema.key_column_usage AS kcu
        ON tc.constraint_name = kcu.constraint_name
        AND tc.table_schema = kcu.table_schema
      JOIN information_schema.constraint_column_usage AS ccu
        ON ccu.constraint_name = tc.constraint_name
        AND ccu.table_schema = tc.table_schema
      JOIN information_schema.referential_constraints AS rc
        ON rc.constraint_name = tc.constraint_name
        AND rc.constraint_schema = tc.table_schema
      WHERE tc.table_schema = 'public'
        AND tc.table_name = ?
        AND tc.constraint_type = 'FOREIGN KEY'
    `,
      [tableName]
    )

    // PostgreSQL raw returns { rows: [...] }
    const fkRows = fkResult.rows || []
    for (const fk of fkRows) {
      foreignKeys.push({
        column: fk.column_name,
        referencedTable: fk.foreign_table_name,
        referencedColumn: fk.foreign_column_name,
        onDelete: fk.delete_rule !== 'NO ACTION' ? fk.delete_rule : undefined,
        onUpdate: fk.update_rule !== 'NO ACTION' ? fk.update_rule : undefined,
      })
    }

    tables.set(tableName, {
      name: tableName,
      columns,
      indexes,
      foreignKeys,
    })
  }

  return { tables }
}

/**
 * Read MySQL schema using information_schema.
 */
async function readMySQLSchema(knex: Knex): Promise<DatabaseSchema> {
  const tables = new Map<string, TableInfo>()

  // Get database name - MySQL raw returns [[rows], fields]
  const dbResult = await knex.raw('SELECT DATABASE() as db_name')
  const dbRows = dbResult[0] || dbResult
  const dbName = (Array.isArray(dbRows) ? dbRows[0] : dbRows)?.db_name || ''

  if (!dbName) {
    throw new Error('Could not determine MySQL database name')
  }

  // Get all tables - use raw query for better control
  const tablesResult = await knex.raw(
    `SELECT TABLE_NAME FROM information_schema.tables WHERE table_schema = ? AND table_type = 'BASE TABLE' ORDER BY TABLE_NAME`,
    [dbName]
  )
  const tableRows = tablesResult[0] || tablesResult

  for (const row of tableRows) {
    const tableName = row.TABLE_NAME || row.table_name
    if (!tableName || isSystemTable(tableName)) continue

    // Get columns using raw query
    const colsResult = await knex.raw(
      `SELECT COLUMN_NAME, DATA_TYPE, IS_NULLABLE, COLUMN_DEFAULT, COLUMN_KEY
       FROM information_schema.columns
       WHERE table_schema = ? AND table_name = ?
       ORDER BY ORDINAL_POSITION`,
      [dbName, tableName]
    )
    const columnRows = colsResult[0] || colsResult

    const columns = new Map<string, ColumnInfo>()
    for (const col of columnRows) {
      const colName = col.COLUMN_NAME || col.column_name
      columns.set(colName, {
        name: colName,
        type: (col.DATA_TYPE || col.data_type || '').toUpperCase(),
        nullable: (col.IS_NULLABLE || col.is_nullable) === 'YES',
        defaultValue: col.COLUMN_DEFAULT || col.column_default,
        primaryKey: (col.COLUMN_KEY || col.column_key) === 'PRI',
      })
    }

    // Get indexes
    const indexes: TableInfo['indexes'] = []
    const idxResult = await knex.raw(
      `SELECT INDEX_NAME, NON_UNIQUE, GROUP_CONCAT(COLUMN_NAME ORDER BY SEQ_IN_INDEX) AS column_names
       FROM information_schema.statistics
       WHERE table_schema = ? AND table_name = ? AND INDEX_NAME != 'PRIMARY'
       GROUP BY INDEX_NAME, NON_UNIQUE`,
      [dbName, tableName]
    )
    const idxRows = idxResult[0] || idxResult
    for (const idx of idxRows) {
      const idxName = idx.INDEX_NAME || idx.index_name
      const nonUnique = idx.NON_UNIQUE ?? idx.non_unique
      const colNames = (idx.column_names || '').split(',')
      indexes.push({
        name: idxName,
        columns: colNames,
        unique: nonUnique === 0,
      })
    }

    // Get foreign keys using raw query (including ON DELETE / ON UPDATE rules)
    const fkResult = await knex.raw(
      `SELECT
         kcu.COLUMN_NAME,
         kcu.REFERENCED_TABLE_NAME,
         kcu.REFERENCED_COLUMN_NAME,
         rc.DELETE_RULE,
         rc.UPDATE_RULE
       FROM information_schema.key_column_usage AS kcu
       JOIN information_schema.referential_constraints AS rc
         ON rc.CONSTRAINT_NAME = kcu.CONSTRAINT_NAME
         AND rc.CONSTRAINT_SCHEMA = kcu.TABLE_SCHEMA
       WHERE kcu.TABLE_SCHEMA = ? AND kcu.TABLE_NAME = ? AND kcu.REFERENCED_TABLE_NAME IS NOT NULL`,
      [dbName, tableName]
    )
    const fkRows = fkResult[0] || fkResult

    const foreignKeys: TableInfo['foreignKeys'] = []
    for (const fk of fkRows) {
      const deleteRule = fk.DELETE_RULE || fk.delete_rule
      const updateRule = fk.UPDATE_RULE || fk.update_rule
      foreignKeys.push({
        column: fk.COLUMN_NAME || fk.column_name,
        referencedTable: fk.REFERENCED_TABLE_NAME || fk.referenced_table_name,
        referencedColumn: fk.REFERENCED_COLUMN_NAME || fk.referenced_column_name,
        onDelete: deleteRule && deleteRule !== 'NO ACTION' ? deleteRule : undefined,
        onUpdate: updateRule && updateRule !== 'NO ACTION' ? updateRule : undefined,
      })
    }

    tables.set(tableName, {
      name: tableName,
      columns,
      indexes,
      foreignKeys,
    })
  }

  return { tables }
}

/**
 * Check if table is a system table (should be ignored in diff).
 */
const SYSTEM_TABLES = new Set([
  '_nexus_migrations',
  '_nexus_migration_lock',
  '_nexus_sequences',
  'single_records',
  'sqlite_sequence', // SQLite internal
  'knex_migrations', // Knex legacy
  'knex_migrations_lock', // Knex legacy
])

export function isSystemTable(tableName: string): boolean {
  return SYSTEM_TABLES.has(tableName)
}
